import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { customerCheckout } from '../../services/api';
import {
  ShoppingCartIcon,
  HeartIcon,
  MapPinIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  ArrowLeftIcon,
  BoltIcon,
  CubeIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  ShieldCheckIcon,
  TruckIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  UserIcon,
  LockClosedIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/* ─────────────────────────────────────────
   Auth helpers – reads/writes to localStorage
   Replace these with real API calls when ready
───────────────────────────────────────── */
const authHelpers = {
  getUser: () => {
    try { return JSON.parse(localStorage.getItem('fh_user') || 'null'); } catch { return null; }
  },
  saveUser: (user) => {
    localStorage.setItem('fh_user', JSON.stringify(user));
  },
  logout: () => {
    localStorage.removeItem('fh_user');
  },
  // Simulated register – replace with real API call
  register: async (data) => {
    await new Promise(r => setTimeout(r, 800)); // simulate network
    const users = JSON.parse(localStorage.getItem('fh_users') || '[]');
    if (users.find(u => u.email === data.email)) {
      throw new Error('Email already registered. Please login.');
    }
    const user = { id: Date.now(), name: data.name, email: data.email, phone: data.phone };
    users.push({ ...user, password: data.password });
    localStorage.setItem('fh_users', JSON.stringify(users));
    return user;
  },
  // Simulated login – replace with real API call
  login: async (email, password) => {
    await new Promise(r => setTimeout(r, 800));
    const users = JSON.parse(localStorage.getItem('fh_users') || '[]');
    const found = users.find(u => u.email === email && u.password === password);
    if (!found) throw new Error('Invalid email or password.');
    return { id: found.id, name: found.name, email: found.email, phone: found.phone };
  },
};

/* ─────────────────────────────────────────
   Auth Modal (Login + Register tabs)
───────────────────────────────────────── */
const AuthModal = ({ onSuccess, onClose }) => {
  const [tab, setTab] = useState('login'); // 'login' | 'register'
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await authHelpers.login(loginForm.email, loginForm.password);
      authHelpers.saveUser(user);
      toast.success(`Welcome back, ${user.name}! 👋`);
      onSuccess(user);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regForm.password !== regForm.confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    if (regForm.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const user = await authHelpers.register(regForm);
      authHelpers.saveUser(user);
      toast.success(`Account created! Welcome, ${user.name}! 🎉`);
      onSuccess(user);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const modalStyles = `
    .auth-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.5);
      backdrop-filter: blur(4px);
      z-index: 200;
      display: flex; align-items: center; justify-content: center;
      padding: 1rem;
    }
    .auth-box {
      background: white;
      border-radius: 2rem;
      padding: 2rem;
      width: 100%;
      max-width: 420px;
      box-shadow: 0 25px 60px rgba(0,0,0,0.2);
      position: relative;
      animation: authSlideUp 0.3s ease-out;
    }
    @keyframes authSlideUp {
      from { opacity: 0; transform: translateY(24px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .auth-close {
      position: absolute; top: 1.25rem; right: 1.25rem;
      background: #f3f4f6; border: none; border-radius: 50%;
      width: 2rem; height: 2rem;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; transition: background 0.2s;
    }
    .auth-close:hover { background: #e5e7eb; }
    .auth-logo { text-align: center; margin-bottom: 1.5rem; }
    .auth-logo h2 {
      font-size: 1.5rem; font-weight: 800;
      background: linear-gradient(135deg, #059669, #0891b2);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }
    .auth-logo p { font-size: 0.85rem; color: #6b7280; margin-top: 0.25rem; }

    .auth-tabs {
      display: flex; border-radius: 9999px;
      background: #f3f4f6; padding: 0.25rem;
      margin-bottom: 1.5rem;
    }
    .auth-tab {
      flex: 1; padding: 0.55rem;
      border: none; border-radius: 9999px;
      font-weight: 700; font-size: 0.9rem;
      cursor: pointer; transition: all 0.25s;
      background: transparent; color: #6b7280;
    }
    .auth-tab.active {
      background: white;
      color: #059669;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }

    .auth-field { margin-bottom: 1rem; }
    .auth-label {
      display: block; font-size: 0.82rem; font-weight: 700;
      color: #374151; margin-bottom: 0.4rem;
    }
    .auth-input-wrap { position: relative; }
    .auth-input-icon {
      position: absolute; left: 0.9rem; top: 50%;
      transform: translateY(-50%);
      width: 1rem; height: 1rem; color: #9ca3af;
      pointer-events: none;
    }
    .auth-input {
      width: 100%; padding: 0.7rem 1rem 0.7rem 2.5rem;
      border: 2px solid #e5e7eb; border-radius: 0.75rem;
      font-size: 0.9rem; transition: border-color 0.2s;
      background: white;
    }
    .auth-input:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }
    .auth-eye {
      position: absolute; right: 0.9rem; top: 50%;
      transform: translateY(-50%);
      background: none; border: none; cursor: pointer;
      color: #9ca3af; padding: 0;
    }
    .auth-eye svg { width: 1rem; height: 1rem; }

    .auth-submit {
      width: 100%;
      background: linear-gradient(135deg, #10b981, #059669);
      color: white; border: none;
      padding: 0.85rem; border-radius: 9999px;
      font-weight: 700; font-size: 1rem;
      cursor: pointer; transition: all 0.3s;
      margin-top: 0.5rem;
      display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    }
    .auth-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 16px rgba(5,150,105,0.3); }
    .auth-submit:disabled { opacity: 0.65; cursor: not-allowed; }

    .auth-switch {
      text-align: center; margin-top: 1rem;
      font-size: 0.85rem; color: #6b7280;
    }
    .auth-switch button {
      color: #059669; font-weight: 700;
      background: none; border: none; cursor: pointer;
    }
  `;

  return (
    <>
      <style>{modalStyles}</style>
      <div className="auth-overlay" onClick={onClose}>
        <div className="auth-box" onClick={e => e.stopPropagation()}>
          <button className="auth-close" onClick={onClose}>
            <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
          </button>

          <div className="auth-logo">
            <h2>🥛 Fresh Hills</h2>
            <p>{tab === 'login' ? 'Sign in to place your order' : 'Create an account to continue'}</p>
          </div>

          <div className="auth-tabs">
            <button className={`auth-tab${tab === 'login' ? ' active' : ''}`} onClick={() => setTab('login')}>Login</button>
            <button className={`auth-tab${tab === 'register' ? ' active' : ''}`} onClick={() => setTab('register')}>Register</button>
          </div>

          {/* ── LOGIN FORM ── */}
          {tab === 'login' && (
            <form onSubmit={handleLogin}>
              <div className="auth-field">
                <label className="auth-label">Email Address</label>
                <div className="auth-input-wrap">
                  <EnvelopeIcon className="auth-input-icon" />
                  <input className="auth-input" type="email" placeholder="your@email.com"
                    value={loginForm.email} onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} required />
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <div className="auth-input-wrap">
                  <LockClosedIcon className="auth-input-icon" />
                  <input className="auth-input" type={showPwd ? 'text' : 'password'} placeholder="Your password"
                    value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} required />
                  <button type="button" className="auth-eye" onClick={() => setShowPwd(p => !p)}>
                    {showPwd ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? <><span className="spin-sm" />Signing in...</> : 'Sign In & Continue'}
              </button>
              <div className="auth-switch">
                Don't have an account?{' '}
                <button type="button" onClick={() => setTab('register')}>Register here</button>
              </div>
            </form>
          )}

          {/* ── REGISTER FORM ── */}
          {tab === 'register' && (
            <form onSubmit={handleRegister}>
              <div className="auth-field">
                <label className="auth-label">Full Name</label>
                <div className="auth-input-wrap">
                  <UserIcon className="auth-input-icon" />
                  <input className="auth-input" type="text" placeholder="Your full name"
                    value={regForm.name} onChange={e => setRegForm(p => ({ ...p, name: e.target.value }))} required />
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">Email Address</label>
                <div className="auth-input-wrap">
                  <EnvelopeIcon className="auth-input-icon" />
                  <input className="auth-input" type="email" placeholder="your@email.com"
                    value={regForm.email} onChange={e => setRegForm(p => ({ ...p, email: e.target.value }))} required />
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">Phone Number</label>
                <div className="auth-input-wrap">
                  <PhoneIcon className="auth-input-icon" />
                  <input className="auth-input" type="tel" placeholder="10-digit mobile number"
                    value={regForm.phone} onChange={e => setRegForm(p => ({ ...p, phone: e.target.value }))} required />
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <div className="auth-input-wrap">
                  <LockClosedIcon className="auth-input-icon" />
                  <input className="auth-input" type={showPwd ? 'text' : 'password'} placeholder="Min 6 characters"
                    value={regForm.password} onChange={e => setRegForm(p => ({ ...p, password: e.target.value }))} required />
                  <button type="button" className="auth-eye" onClick={() => setShowPwd(p => !p)}>
                    {showPwd ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">Confirm Password</label>
                <div className="auth-input-wrap">
                  <LockClosedIcon className="auth-input-icon" />
                  <input className="auth-input" type="password" placeholder="Repeat password"
                    value={regForm.confirm} onChange={e => setRegForm(p => ({ ...p, confirm: e.target.value }))} required />
                </div>
              </div>
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? <><span className="spin-sm" />Creating Account...</> : 'Create Account & Continue'}
              </button>
              <div className="auth-switch">
                Already have an account?{' '}
                <button type="button" onClick={() => setTab('login')}>Login here</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────
   Main Checkout Component
───────────────────────────────────────── */
const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
  });

  // On mount: check if user already logged in
  useEffect(() => {
    const user = authHelpers.getUser();
    if (user) {
      setCurrentUser(user);
      // Pre-fill form with saved user data
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      }));
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If cart is empty redirect
  useEffect(() => {
    if (cart.length === 0) navigate('/products');
  }, [cart, navigate]);

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Called when auth modal login/register succeeds
  const handleAuthSuccess = (user) => {
    setCurrentUser(user);
    setShowAuthModal(false);
    setFormData(prev => ({
      ...prev,
      name: user.name || prev.name,
      email: user.email || prev.email,
      phone: user.phone || prev.phone,
    }));
  };

  const handleLogout = () => {
    authHelpers.logout();
    setCurrentUser(null);
    setFormData(prev => ({ ...prev, name: '', email: '', phone: '' }));
    toast.success('Logged out successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Require login before placing order
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        ...formData,
        user_id: currentUser.id,
        total_amount: getCartTotal(),
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity || 1,
          price: item.price || item.priceValue || 0,
        })),
      };

      const response = await customerCheckout(orderData);

      if (response.data.success) {
        clearCart();
        navigate(`/order-confirmation/${response.data.order_number}`);
        toast.success('Order placed successfully! 🎉');
      }
    } catch (error) {
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cartCount = cart.reduce((total, item) => total + (item.quantity || 1), 0);

  if (cart.length === 0) return null;

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');

    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif;
      color: #1f2937;
      background: #f9fafb;
    }

    .fh-container {
      width: 100%; max-width: 1280px;
      margin: 0 auto; padding: 0 1rem;
    }
    @media(min-width:640px){ .fh-container{ padding: 0 1.5rem; } }
    @media(min-width:1024px){ .fh-container{ padding: 0 2rem; } }

    /* ── HEADER ── */
    .fh-header {
      position: sticky; top: 0; z-index: 50;
      width: 100%; transition: all 0.35s ease;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(12px);
      border-bottom: 1px solid rgba(229,231,235,0.6);
    }
    .fh-header.scrolled {
      box-shadow: 0 4px 20px rgba(0,0,0,0.07);
      border-bottom-color: rgba(5,150,105,0.15);
    }
    .fh-header-inner {
      display: flex; align-items: center; justify-content: space-between;
      height: 60px;
    }
    @media(min-width:768px){ .fh-header-inner{ height: 72px; } }

    .fh-logo { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
    .fh-logo img { height: 36px; }
    @media(min-width:768px){ .fh-logo img{ height: 44px; } }
    .fh-logo-text h1 {
      font-size: 1rem; font-weight: 800;
      background: linear-gradient(135deg,#059669,#0891b2);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }
    @media(min-width:768px){ .fh-logo-text h1{ font-size: 1.4rem; } }
    .fh-logo-text span { display: none; font-size: 0.65rem; color: #6b7280; }
    @media(min-width:480px){ .fh-logo-text span{ display: block; } }

    .fh-nav { display: none; }
    @media(min-width:768px){ .fh-nav{ display: flex; align-items: center; gap: 1.75rem; } }
    .fh-nav-link {
      color: #374151; font-weight: 600; font-size: 0.9rem;
      text-decoration: none; transition: color 0.2s;
    }
    .fh-nav-link:hover { color: #059669; }

    .fh-header-actions { display: flex; align-items: center; gap: 0.5rem; }
    .fh-cart-icon-btn {
      position: relative;
      width: 38px; height: 38px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%; background: #f3f4f6;
      border: none; cursor: pointer; transition: background 0.2s;
    }
    .fh-cart-icon-btn:hover { background: #e5e7eb; }
    .fh-cart-icon-btn svg { width: 1.1rem; height: 1.1rem; color: #374151; }
    .fh-cart-badge {
      position: absolute; top: -4px; right: -4px;
      background: #ef4444; color: white;
      font-size: 0.6rem; font-weight: 800;
      width: 1.1rem; height: 1.1rem;
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      border: 2px solid white;
    }

    /* User info in header */
    .fh-user-chip {
      display: flex; align-items: center; gap: 0.5rem;
      background: #ecfdf5;
      padding: 0.35rem 0.9rem;
      border-radius: 9999px;
      font-size: 0.82rem;
      font-weight: 700;
      color: #047857;
    }
    .fh-user-chip svg { width: 0.85rem; height: 0.85rem; }
    .fh-logout-btn {
      background: none; border: none; cursor: pointer;
      color: #ef4444; font-size: 0.75rem; font-weight: 700;
      padding: 0.2rem 0.5rem; border-radius: 9999px;
      transition: background 0.2s;
    }
    .fh-logout-btn:hover { background: #fee2e2; }

    /* ── AUTH PROMPT BANNER ── */
    .fh-auth-prompt {
      background: linear-gradient(135deg, #fff7ed, #fffbeb);
      border: 1px solid #fed7aa;
      border-radius: 1rem;
      padding: 1rem 1.25rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }
    .fh-auth-prompt-text { font-size: 0.9rem; color: #92400e; font-weight: 600; }
    .fh-auth-prompt-btns { display: flex; gap: 0.5rem; flex-wrap: wrap; }
    .fh-auth-btn-login {
      background: #059669; color: white;
      border: none; border-radius: 9999px;
      padding: 0.45rem 1.1rem;
      font-weight: 700; font-size: 0.82rem;
      cursor: pointer; transition: all 0.2s;
    }
    .fh-auth-btn-login:hover { background: #047857; }
    .fh-auth-btn-register {
      background: white; color: #059669;
      border: 2px solid #d1fae5; border-radius: 9999px;
      padding: 0.45rem 1.1rem;
      font-weight: 700; font-size: 0.82rem;
      cursor: pointer; transition: all 0.2s;
    }
    .fh-auth-btn-register:hover { background: #f0fdf4; }

    /* ── CHECKOUT LAYOUT ── */
    .fh-checkout-wrap {
      max-width: 1100px; margin: 0 auto; padding: 2rem 1rem;
    }
    @media(min-width:640px){ .fh-checkout-wrap{ padding: 2.5rem 1.5rem; } }

    .fh-checkout-grid {
      display: grid; gap: 2rem;
    }
    @media(min-width:1024px){
      .fh-checkout-grid { grid-template-columns: 1.5fr 1fr; }
    }

    .fh-checkout-title {
      font-size: 1.8rem; font-weight: 900;
      background: linear-gradient(135deg,#059669,#0891b2);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      margin-bottom: 1.5rem;
      display: flex; align-items: center; gap: 0.75rem;
    }

    .fh-back-btn {
      display: inline-flex; align-items: center; gap: 0.5rem;
      color: #4b5563; background: none; border: none;
      cursor: pointer; font-size: 0.9rem;
      padding: 0.5rem 1rem; border-radius: 9999px;
      transition: all 0.2s; margin-bottom: 1.25rem;
    }
    .fh-back-btn:hover { background: #f3f4f6; color: #059669; }
    .fh-back-btn svg { width: 1rem; height: 1rem; }

    /* ── CARD ── */
    .fh-card {
      background: white; border-radius: 1.5rem;
      padding: 1.75rem;
      box-shadow: 0 4px 20px rgba(0,0,0,0.03);
      border: 1px solid #f3f4f6;
    }
    .fh-card-title {
      font-size: 1.1rem; font-weight: 800;
      color: #1f2937; margin-bottom: 1.25rem;
      display: flex; align-items: center; gap: 0.6rem;
    }
    .fh-card-title svg { width: 1.25rem; height: 1.25rem; color: #059669; }

    /* ── FORM ── */
    .fh-form-grid-2 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
    @media(min-width:480px){ .fh-form-grid-2{ grid-template-columns: 1fr 1fr; } }

    .fh-form-field { margin-bottom: 0; }
    .fh-form-label {
      display: block; font-size: 0.82rem; font-weight: 700;
      color: #374151; margin-bottom: 0.4rem;
    }
    .fh-form-input {
      width: 100%; padding: 0.7rem 1rem;
      border: 2px solid #e5e7eb; border-radius: 0.75rem;
      font-size: 0.9rem; transition: border-color 0.2s;
      font-family: inherit;
    }
    .fh-form-input:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }
    .fh-form-textarea {
      width: 100%; padding: 0.7rem 1rem;
      border: 2px solid #e5e7eb; border-radius: 0.75rem;
      font-size: 0.9rem; transition: border-color 0.2s;
      resize: vertical; font-family: inherit;
    }
    .fh-form-textarea:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }

    /* ── ORDER SUMMARY ── */
    .fh-summary-sticky { position: sticky; top: 80px; }
    .fh-summary-item {
      display: flex; justify-content: space-between;
      align-items: flex-start; padding: 0.65rem 0;
      border-bottom: 1px solid #f3f4f6; font-size: 0.9rem;
    }
    .fh-summary-item:last-child { border-bottom: none; }
    .fh-summary-name { color: #4b5563; flex: 1; padding-right: 0.5rem; }
    .fh-summary-qty { color: #9ca3af; font-size: 0.8rem; }
    .fh-summary-price { font-weight: 700; color: #1f2937; white-space: nowrap; }
    .fh-summary-divider { border: none; border-top: 2px solid #f3f4f6; margin: 0.75rem 0; }
    .fh-summary-total {
      display: flex; justify-content: space-between;
      font-size: 1.2rem; font-weight: 900; color: #1f2937;
    }
    .fh-summary-total-amount { color: #059669; }

    /* ── PLACE ORDER BTN ── */
    .fh-place-btn {
      width: 100%;
      background: linear-gradient(135deg,#059669,#10b981);
      color: white; border: none;
      padding: 1rem; border-radius: 9999px;
      font-weight: 800; font-size: 1.05rem;
      margin-top: 1.25rem; cursor: pointer;
      transition: all 0.3s;
      display: flex; align-items: center; justify-content: center; gap: 0.6rem;
      box-shadow: 0 4px 14px rgba(5,150,105,0.25);
    }
    .fh-place-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(5,150,105,0.3); }
    .fh-place-btn:disabled { opacity: 0.6; cursor: not-allowed; }

    .fh-delivery-note {
      display: flex; align-items: center; gap: 0.5rem;
      background: #ecfdf5; padding: 0.75rem 1rem;
      border-radius: 0.75rem; margin-top: 1rem;
      font-size: 0.82rem; color: #065f46; font-weight: 600;
    }
    .fh-delivery-note svg { width: 1rem; height: 1rem; flex-shrink: 0; }

    /* Spinner */
    @keyframes spin { to { transform: rotate(360deg); } }
    .spin-sm {
      width: 1.1rem; height: 1.1rem;
      border: 2px solid rgba(255,255,255,0.4);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
      display: inline-block;
    }

    /* ── TRUST BAR ── */
    .fh-trust-bar {
      background: #f9fafb;
      padding: 1.25rem 0;
      border-top: 1px solid #e5e7eb;
      margin-top: 3rem;
    }
    .fh-trust-items {
      display: flex; flex-wrap: wrap;
      align-items: center; justify-content: center;
      gap: 1rem 2rem;
    }
    .fh-trust-item { display: flex; align-items: center; gap: 0.5rem; }
    .fh-trust-icon-box {
      width: 1.75rem; height: 1.75rem;
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
    }
    .fh-trust-item span { font-size: 0.82rem; font-weight: 600; color: #374151; }

    /* ── FOOTER ── */
    .fh-footer { background: #0f172a; color: white; padding: 4rem 0 2rem; margin-top: 4rem; }
    .fh-footer-grid {
      display: grid; grid-template-columns: 1fr; gap: 2.5rem; margin-bottom: 3rem;
    }
    @media(min-width:640px){ .fh-footer-grid{ grid-template-columns: repeat(2,1fr); } }
    @media(min-width:1024px){ .fh-footer-grid{ grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 3rem; } }

    .fh-footer-logo { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.25rem; cursor: pointer; }
    .fh-footer-logo img { height: 44px; }
    .fh-footer-logo h3 {
      font-size: 1.35rem; font-weight: 800;
      background: linear-gradient(135deg,#34d399,#22d3ee);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }
    .fh-footer-about { color: #94a3b8; line-height: 1.7; font-size: 0.9rem; margin-bottom: 1.25rem; }
    .fh-social-row { display: flex; gap: 0.75rem; }
    .fh-social-btn {
      width: 2.25rem; height: 2.25rem; background: #1e293b;
      border-radius: 0.65rem; display: flex; align-items: center; justify-content: center;
      color: white; transition: all 0.3s; text-decoration: none;
    }
    .fh-social-btn:hover { background: #2563eb; transform: translateY(-2px); }
    .fh-social-btn svg { width: 1.1rem; height: 1.1rem; }

    .fh-footer-col-title {
      font-size: 1rem; font-weight: 700; color: white;
      margin-bottom: 1.25rem; display: flex; align-items: center; gap: 0.4rem;
    }
    .fh-footer-col-title svg { width: 1rem; height: 1rem; color: #ef4444; }

    .fh-footer-links { list-style: none; }
    .fh-footer-links li { margin-bottom: 0.75rem; }
    .fh-footer-links a {
      color: #94a3b8; text-decoration: none; font-size: 0.9rem;
      transition: all 0.2s; display: inline-flex; align-items: center; gap: 0.35rem;
    }
    .fh-footer-links a:hover { color: white; transform: translateX(4px); }

    .fh-footer-contact-list { list-style: none; }
    .fh-footer-contact-list li {
      display: flex; align-items: flex-start; gap: 0.65rem;
      color: #94a3b8; font-size: 0.9rem; margin-bottom: 0.85rem;
    }
    .fh-footer-contact-list li svg { width: 1.1rem; height: 1.1rem; flex-shrink: 0; margin-top: 0.1rem; }

    .fh-newsletter-row { display: flex; gap: 0.4rem; margin-top: 0.75rem; }
    .fh-newsletter-row input {
      flex: 1; padding: 0.65rem 1rem; border-radius: 9999px;
      border: 1px solid #334155; background: #1e293b; color: white;
      font-size: 0.85rem; min-width: 0;
    }
    .fh-newsletter-row input::placeholder { color: #64748b; }
    .fh-newsletter-row button {
      background: #2563eb; color: white; border: none;
      border-radius: 9999px; padding: 0.65rem 1.1rem;
      font-weight: 700; font-size: 0.82rem; cursor: pointer; white-space: nowrap;
    }

    .fh-footer-bottom {
      border-top: 1px solid #1e293b; padding-top: 2rem;
      display: flex; flex-direction: column; align-items: center; gap: 1rem; text-align: center;
    }
    @media(min-width:768px){ .fh-footer-bottom{ flex-direction: row; justify-content: space-between; text-align: left; } }
    .fh-copyright { color: #64748b; font-size: 0.82rem; }
    .fh-footer-bottom-links { display: flex; flex-wrap: wrap; gap: 1.5rem; justify-content: center; }
    .fh-footer-bottom-links a { color: #94a3b8; text-decoration: none; font-size: 0.82rem; }
    .fh-footer-bottom-links a:hover { color: white; }
    .fh-seo-link { color: #f59e0b !important; font-weight: 700; display: inline-flex; align-items: center; gap: 0.35rem; }
    @keyframes heartbeat { 0%,100%{transform:scale(1);} 25%,75%{transform:scale(1.15);} }
    .animate-heart { animation: heartbeat 1.4s ease-in-out infinite; }
  `;

  return (
    <>
      <style>{styles}</style>

      {/* ── AUTH MODAL ── */}
      {showAuthModal && (
        <AuthModal
          onSuccess={handleAuthSuccess}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        {/* ── HEADER ── */}
        <header className={`fh-header${scrolled ? ' scrolled' : ''}`}>
          <div className="fh-container">
            <div className="fh-header-inner">
              <div className="fh-logo" onClick={() => navigate('/')}>
                <img src="/images/logo.png" alt="Fresh Hills"
                  onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/44?text=FH'; }} />
                <div className="fh-logo-text">
                  <h1>Fresh Hills</h1>
                  <span>Fresh & Healthy Since 2024</span>
                </div>
              </div>
              <nav className="fh-nav">
                <a href="/" className="fh-nav-link">Home</a>
                <a href="/products" className="fh-nav-link">Products</a>
                <a href="/#offers" className="fh-nav-link">Offers</a>
              </nav>
              <div className="fh-header-actions">
                {currentUser && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span className="fh-user-chip">
                      <UserIcon style={{ width: '0.85rem' }} />
                      {currentUser.name.split(' ')[0]}
                    </span>
                    <button className="fh-logout-btn" onClick={handleLogout}>Logout</button>
                  </div>
                )}
                {!currentUser && (
                  <button className="fh-auth-btn-login" onClick={() => setShowAuthModal(true)}>
                    Login / Register
                  </button>
                )}
                <button className="fh-cart-icon-btn" onClick={() => navigate('/cart')}>
                  <ShoppingCartIcon />
                  {cartCount > 0 && <span className="fh-cart-badge">{cartCount > 9 ? '9+' : cartCount}</span>}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* ── MAIN CHECKOUT ── */}
        <div className="fh-checkout-wrap">
          <button onClick={() => navigate('/cart')} className="fh-back-btn">
            <ArrowLeftIcon />
            Back to Cart
          </button>

          <h1 className="fh-checkout-title">
            🛒 Checkout
          </h1>

          {/* Auth prompt banner if not logged in */}
          {!currentUser && (
            <div className="fh-auth-prompt">
              <span className="fh-auth-prompt-text">
                🔐 Please login or register to place your order
              </span>
              <div className="fh-auth-prompt-btns">
                <button className="fh-auth-btn-login" onClick={() => setShowAuthModal(true)}>Login</button>
                <button className="fh-auth-btn-register" onClick={() => setShowAuthModal(true)}>Register</button>
              </div>
            </div>
          )}

          <div className="fh-checkout-grid">
            {/* ── LEFT: Delivery Form ── */}
            <div>
              <div className="fh-card">
                <h2 className="fh-card-title">
                  <TruckIcon />
                  Delivery Information
                </h2>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="fh-form-grid-2">
                    <div className="fh-form-field">
                      <label className="fh-form-label">Full Name *</label>
                      <input name="name" type="text" className="fh-form-input"
                        placeholder="Your full name"
                        value={formData.name} onChange={handleInputChange} required />
                    </div>
                    <div className="fh-form-field">
                      <label className="fh-form-label">Email *</label>
                      <input name="email" type="email" className="fh-form-input"
                        placeholder="your@email.com"
                        value={formData.email} onChange={handleInputChange} required />
                    </div>
                  </div>

                  <div className="fh-form-field">
                    <label className="fh-form-label">Phone Number *</label>
                    <input name="phone" type="tel" className="fh-form-input"
                      placeholder="10-digit mobile number"
                      value={formData.phone} onChange={handleInputChange} required />
                  </div>

                  <div className="fh-form-field">
                    <label className="fh-form-label">Delivery Address *</label>
                    <textarea name="address" rows="3" className="fh-form-textarea"
                      placeholder="House/Flat No., Building, Street, Landmark"
                      value={formData.address} onChange={handleInputChange} required />
                  </div>

                  <div className="fh-form-grid-2">
                    <div className="fh-form-field">
                      <label className="fh-form-label">City *</label>
                      <input name="city" type="text" className="fh-form-input"
                        placeholder="City"
                        value={formData.city} onChange={handleInputChange} required />
                    </div>
                    <div className="fh-form-field">
                      <label className="fh-form-label">Pincode *</label>
                      <input name="pincode" type="text" className="fh-form-input"
                        placeholder="6-digit pincode"
                        value={formData.pincode} onChange={handleInputChange} required />
                    </div>
                  </div>

                  <button type="submit" className="fh-place-btn" disabled={loading}>
                    {loading
                      ? <><span className="spin-sm" /> Placing Order...</>
                      : !currentUser
                        ? '🔐 Login to Place Order'
                        : `✅ Place Order • ₹${getCartTotal()}`
                    }
                  </button>
                </form>
              </div>
            </div>

            {/* ── RIGHT: Order Summary ── */}
            <div className="fh-summary-sticky">
              <div className="fh-card">
                <h2 className="fh-card-title">
                  <ShoppingCartIcon />
                  Order Summary
                </h2>

                <div style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                  {cart.map((item, idx) => (
                    <div key={idx} className="fh-summary-item">
                      <div className="fh-summary-name">
                        {item.name}
                        <div className="fh-summary-qty">×{item.quantity || 1}</div>
                      </div>
                      <span className="fh-summary-price">
                        ₹{(item.price || item.priceValue || 0) * (item.quantity || 1)}
                      </span>
                    </div>
                  ))}
                </div>

                <hr className="fh-summary-divider" />

                <div className="fh-summary-item" style={{ borderBottom: 'none' }}>
                  <span style={{ color: '#6b7280' }}>Delivery</span>
                  <span style={{ color: '#10b981', fontWeight: 700 }}>FREE</span>
                </div>

                <div className="fh-summary-total">
                  <span>Total</span>
                  <span className="fh-summary-total-amount">₹{getCartTotal()}</span>
                </div>

                <div className="fh-delivery-note">
                  <TruckIcon />
                  Free delivery · Est. 30–45 min
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', fontSize: '0.78rem', color: '#6b7280' }}>
                  <ShieldCheckIcon style={{ width: '1rem', height: '1rem' }} />
                  100% secure payment · Cashless delivery
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── TRUST BAR ── */}
        <div className="fh-trust-bar">
          <div className="fh-container">
            <div className="fh-trust-items">
              {[
                { bg: '#d1fae5', icon: <BoltIcon style={{ width: '1rem', height: '1rem', color: '#059669' }} />, label: 'Same Day Delivery' },
                { bg: '#dbeafe', icon: <CurrencyRupeeIcon style={{ width: '1rem', height: '1rem', color: '#2563eb' }} />, label: 'Cashless Payment' },
                { bg: '#fef3c7', icon: <CubeIcon style={{ width: '1rem', height: '1rem', color: '#d97706' }} />, label: 'Fresh Stock Daily' },
                { bg: '#fae8ff', icon: <UserGroupIcon style={{ width: '1rem', height: '1rem', color: '#9333ea' }} />, label: '10k+ Customers' },
              ].map(t => (
                <div key={t.label} className="fh-trust-item">
                  <div className="fh-trust-icon-box" style={{ background: t.bg }}>{t.icon}</div>
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer id="contact" className="fh-footer">
          <div className="fh-container">
            <div className="fh-footer-grid">
              <div>
                <div className="fh-footer-logo" onClick={() => navigate('/')}>
                  <img src="/images/logo.png" alt="Fresh Hills"
                    onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/44?text=FH'; }} />
                  <h3>Fresh Hills</h3>
                </div>
                <p className="fh-footer-about">India's trusted online dairy store — fresh milk, eggs, butter and more delivered daily since 2024.</p>
                <div className="fh-social-row">
                  {['fb','tw','ig'].map(s => (
                    <a key={s} href="#" className="fh-social-btn">
                      <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: '1rem', height: '1rem' }}>
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="fh-footer-col-title"><HeartIcon />Quick Links</h4>
                <ul className="fh-footer-links">
                  {[['/',  'Home'], ['/products', 'Products'], ['/#offers', 'Offers'], ['/#contact', 'Contact']].map(([href, lbl]) => (
                    <li key={lbl}><a href={href}><ChevronRightIcon style={{ width: '0.7rem' }} />{lbl}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="fh-footer-col-title"><HeartIcon />Our Products</h4>
                <ul className="fh-footer-links">
                  {['Fresh Milk','Butter & Cream','Cheese','Farm Eggs','Curd & Yogurt'].map(p => (
                    <li key={p}><a href="/products"><ChevronRightIcon style={{ width: '0.7rem' }} />{p}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="fh-footer-col-title"><HeartIcon />Contact Us</h4>
                <ul className="fh-footer-contact-list">
                  <li><MapPinIcon /><span>36/845, Fesicon Enterprises, Near Sunrise Hospital, Sea Port - Air Port Road, Kakkanad, Thrikkakara, Ernakulam, Kerala, 682021</span></li>
                  <li><DevicePhoneMobileIcon /><span>+91 70108 44160</span></li>
                  <li>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '1.1rem', height: '1.1rem' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>hello@fesiconenterprises.com</span>
                  </li>
                </ul>
                <div>
                  <p style={{ color: 'white', fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem' }}>Subscribe for offers</p>
                  <div className="fh-newsletter-row">
                    <input type="email" placeholder="Your email address" />
                    <button>Go</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="fh-footer-bottom">
              <div className="fh-copyright">
                <span>© {currentYear} FESICON ENTERPRISE. All rights reserved. ❤️ Made in India</span>
                <br />
                <span style={{ fontSize: '0.7rem', display: 'block', marginTop: '0.25rem' }}>
                  GST: 32AAGFF6427Q1Z2
                </span>
              </div>
              <div className="fh-footer-bottom-links">
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
                <a href="https://seo9ine.com" target="_blank" rel="noopener noreferrer" className="fh-seo-link">
                  SEO9INE <HeartIcon className="animate-heart" style={{ width: '0.85rem', height: '0.85rem', color: '#ef4444' }} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Checkout;